/**
 * Aged no-touch claims (review #695, rewritten by Shayna 2026-10-18; MEASURE-RULES
 * Rule 5.7).
 *
 * A resolved claim with no FCC touch that took more than its PAYER TYPE's limit, in days
 * from its send date (the clearinghouse release, else the service month's last day; the
 * bill date on bundles before #911, `Dataset.agedFrom`) to resolution, is taken as worked
 * outside the FCC aging workflow tool. It
 * is excluded from the 0% touch share ENTIRELY, numerator and base, and shows on Touch
 * profile as a bucket of its own. (Until 2026-10-19 it was per department and stayed in
 * the base as a worked claim.)
 *
 * Each payer type's limit is set on the Rules page (Payer types tab,
 * rules.payer_type_setting, `zero_touch_max_age_days`) and read by every viewer from
 * /api/payer-type-settings. No limit set, or the settings unreadable: no limit.
 *
 * The bundle carries the no-touch claims resolved more than d days after that date for
 * each of AGE_STEPS, by payer type (an index into `Dataset.agedPayerTypes`): on each
 * month row (`agedNoTouch`), each person's claim cells (`a`) and each facility row's
 * tail. The app picks each payer type's step, so a limit changed on the Rules page
 * reaches every figure with no rebuild.
 *
 * Applied ONCE, to the loaded dataset (DatasetProvider), so every page reads the same
 * figures: `resolvedNoTouch` becomes the no-touch claims within their limits, and the
 * aged ones move to `resolvedNoTouchAged`, which no touch share's base counts. The
 * bundle's own objects are never written to.
 */
import type { ClaimCellDict, Dataset, EmployeeRow, FacilityMonthsDict, TouchMonthRow } from './types'

/** The days a payer type can have. build_bundle.py AGE_STEPS and api/shared/rules.ts list the same. */
export const AGE_STEPS = [30, 45, 60, 90, 120, 180] as const
export type AgeStep = (typeof AGE_STEPS)[number]

export const isAgeStep = (n: unknown): n is AgeStep => (AGE_STEPS as readonly unknown[]).includes(n)

/** The setting's key in rules.payer_type_setting. */
export const AGE_SETTING = 'zero_touch_max_age_days'

/** Payer type → its limit. A payer type not in the map has none. */
export type AgeLimits = ReadonlyMap<string, AgeStep>

/** The limits out of /api/payer-type-settings' rows. Anything not a step is ignored. */
export function ageLimitsFrom(rows: readonly { payerType: string; setting: string; value: number }[]): AgeLimits {
  const out = new Map<string, AgeStep>()
  for (const r of rows) if (r.setting === AGE_SETTING && isAgeStep(r.value)) out.set(r.payerType, r.value)
  return out
}

/**
 * A month row's `agedNoTouch` group: [payer type, no touch, touched, over 30 … 180].
 * Where the counts start in it.
 */
export const GROUP_OVER = 3

/** True when the bundle carries the aged counts at all (built since the rework). */
export const hasAgedCounts = (d: Pick<Dataset, 'agedPayerTypes'>): boolean => Array.isArray(d.agedPayerTypes)

/** Payer type index → which step's count to take, for the payer types with a limit. */
function stepsByIndex(types: readonly string[], limits: AgeLimits): Map<number, number> {
  const out = new Map<number, number>()
  types.forEach((t, i) => {
    const x = limits.get(t)
    if (x !== undefined) out.set(i, AGE_STEPS.indexOf(x))
  })
  return out
}

/**
 * The dataset with each payer type's limit applied. The same object when no payer type
 * has a limit, or the bundle has no aged counts, so nothing re-derives.
 */
export function applyAgedNoTouch(d: Dataset, limits: AgeLimits): Dataset {
  if (limits.size === 0 || !d.agedPayerTypes) return d
  const steps = stepsByIndex(d.agedPayerTypes, limits)
  if (steps.size === 0) return d

  const touchMonths = d.touchMonths.map((r): TouchMonthRow => {
    let aged = 0
    for (const g of r.agedNoTouch ?? []) {
      const k = steps.get(g[0]!)
      if (k !== undefined) aged += g[GROUP_OVER + k] ?? 0
    }
    aged = Math.min(aged, r.resolvedNoTouch)
    return aged === 0 ? r : { ...r, resolvedNoTouch: r.resolvedNoTouch - aged, resolvedNoTouchAged: aged }
  })

  const cellsOut = applyToCells(d.employees, d.claimCells, steps)
  const facilityMonths = d.facilityMonths ? applyToFacilities(d.facilityMonths, steps) : d.facilityMonths

  return {
    ...d,
    touchMonths,
    ...(cellsOut ? { employees: cellsOut.employees, claimCells: cellsOut.dict } : {}),
    ...(facilityMonths ? { facilityMonths } : {}),
  }
}

/** The claim-filter cells: each person's `a` rows ([month, cell, payer type, over 30 … 180]) taken off their month rows. */
function applyToCells(
  employees: readonly EmployeeRow[],
  dict: ClaimCellDict | undefined,
  steps: ReadonlyMap<number, number>,
): { employees: EmployeeRow[]; dict: ClaimCellDict } | null {
  if (!dict) return null
  const noTouchAt = dict.monthFields.indexOf('resolvedNoTouch')
  if (noTouchAt < 0) return null
  const width = 2 + dict.monthFields.length
  const out = employees.map((e) => {
    if (!e.cells?.a || !e.cells.m) return e
    const agedBy = new Map<string, number>()
    for (const a of e.cells.a) {
      const k = steps.get(a[2]!)
      if (k === undefined) continue
      const n = a[3 + k] ?? 0
      if (n) agedBy.set(`${a[0]}|${a[1]}`, (agedBy.get(`${a[0]}|${a[1]}`) ?? 0) + n)
    }
    if (agedBy.size === 0) return e
    const m = e.cells.m.map((row) => {
      const aged = Math.min(agedBy.get(`${row[0]}|${row[1]}`) ?? 0, row[2 + noTouchAt] ?? 0)
      if (aged === 0) return row
      const full = [...row, ...Array<number>(Math.max(0, width - row.length)).fill(0)]
      full[2 + noTouchAt] = full[2 + noTouchAt]! - aged
      // resolvedNoTouchAged, appended to monthFields below.
      full[width] = aged
      return full
    })
    return { ...e, cells: { ...e.cells, m } }
  })
  return { employees: out, dict: { ...dict, monthFields: [...dict.monthFields, 'resolvedNoTouchAged'] } }
}

/** Where a facility row's aged groups start: after the cell (13). Groups of [payer type, over 30 … 180]. */
export const FACILITY_AGED_FROM = 14
const GROUP = 1 + AGE_STEPS.length

/** The Facilities page's rows: noTouch (8) less the aged, which leave its 0% touch share. */
function applyToFacilities(fm: FacilityMonthsDict, steps: ReadonlyMap<number, number>): FacilityMonthsDict {
  let changed = false
  const rows = fm.rows.map((r) => {
    let aged = 0
    for (let i = FACILITY_AGED_FROM; i + GROUP <= r.length; i += GROUP) {
      const k = steps.get(r[i]!)
      if (k !== undefined) aged += r[i + 1 + k] ?? 0
    }
    aged = Math.min(aged, r[8] ?? 0)
    if (aged === 0) return r
    changed = true
    const copy: number[] = [...r]
    copy[8] = copy[8]! - aged
    return copy
  })
  return changed ? { ...fm, rows } : fm
}

/**
 * A payer type's resolved claims in the rows given: no touch, touched, and the no-touch
 * ones over each step, off the bundle's own `agedNoTouch` groups (the limits applied or
 * not). What the Rules page previews each limit with.
 */
export function payerTypeTotals(
  rows: readonly Pick<TouchMonthRow, 'agedNoTouch'>[],
  types: readonly string[],
): Map<string, { noTouch: number; touched: number; over: number[] }> {
  const out = new Map<string, { noTouch: number; touched: number; over: number[] }>()
  for (const r of rows)
    for (const g of r.agedNoTouch ?? []) {
      const t = types[g[0]!]
      if (t === undefined) continue
      let e = out.get(t)
      if (!e) out.set(t, (e = { noTouch: 0, touched: 0, over: AGE_STEPS.map(() => 0) }))
      e.noTouch += g[1] ?? 0
      e.touched += g[2] ?? 0
      AGE_STEPS.forEach((_, k) => (e!.over[k]! += g[GROUP_OVER + k] ?? 0))
    }
  return out
}
